import { type ServicesTab_userProfile$key } from '#app/__generated__/relay/ServicesTab_userProfile.graphql.js';
import { useProfileMagicServicesTabIndicator } from '#app/components/ContraMagic/useProfileMagicServicesTabIndicator.js';
import { RoutingTabs } from '#app/components/RoutingTabs/RoutingTabs.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useRouter } from '#app/hooks/useRouter.js';
import { SparklesEmoji } from '@contra/icons/emojis';
import { graphql, useFragment } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { styled } from 'styled-components';

type ServicesTabProps = {
  readonly userProfileRef: ServicesTab_userProfile$key;
};

const ProfileMagicIndicatorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export const ServicesTab = ({ userProfileRef }: ServicesTabProps) => {
  const userProfile = useFragment<ServicesTab_userProfile$key>(
    graphql`
      fragment ServicesTab_userProfile on UserProfile {
        displayUsername
        visitorCanEdit
        analyticsId
      }
    `,
    userProfileRef,
  );
  const { analyticsId, displayUsername, visitorCanEdit } = userProfile;
  const { isActive } = useRouter();
  const { track } = useAnalytics();

  const {
    hasUsedProfileMagic,
    hideProfileMagicServiceIndicator,
    shouldHideProfileMagicServiceIndicator,
  } = useProfileMagicServicesTabIndicator();

  const isServicesTabActive = isActive(
    getRoute('/@username/services', {
      args: {
        username: displayUsername,
      },
    }),
    false,
  );

  return (
    <RoutingTabs.Tab
      match={() => isServicesTabActive}
      onClick={() => {
        if (userProfile) {
          track('tab', {
            action: 'tapped',
            is_owner: visitorCanEdit,
            owner_id: analyticsId,
            tab_name: 'profile_services',
          });
        }

        if (visitorCanEdit) {
          hideProfileMagicServiceIndicator();
        }
      }}
      to={getRoute('/@username/services', {
        args: {
          username: displayUsername,
        },
      })}
    >
      {visitorCanEdit &&
      hasUsedProfileMagic &&
      !shouldHideProfileMagicServiceIndicator &&
      !isServicesTabActive ? (
        <ProfileMagicIndicatorContainer aria-label="New Service Suggestions">
          <SparklesEmoji size={16} />
          Services
        </ProfileMagicIndicatorContainer>
      ) : (
        'Services'
      )}
    </RoutingTabs.Tab>
  );
};
